// Implements: F-IDN-012, F-UIX-009
/**
 * The Contraseña section: owns the change and decides what each answer means.
 *
 * A `401` here is a wrong CURRENT password, never an expired session: the route is exempt from
 * the client's sign-out redirect (httpClient `UNAUTHORIZED_EXEMPT`, H-F6), so the person stays on
 * `/cuenta/contrasena` and reads the v3 sentence at the top of the form. A `422` that is the
 * password policy refusing the NEW password (too short, padded with spaces, the same as the current
 * one; `isPolicyRefusal` says which `422` that is) goes under the new-password field as the server
 * wrote it; any other `422` (the request schema) is said for the whole form. A `429` is a wait; any
 * other refusal is the generic error notice.
 */

import { useCallback, useState } from "react";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { useRateLimitWait } from "../../../shared/api/useRateLimitWait";
import { useTranslation } from "../../../shared/i18n";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { PasswordForm } from "../components/PasswordForm";
import type { PasswordSubmitOutcome } from "../components/PasswordForm";
import { dateText } from "../model/dates";
import { isPolicyRefusal } from "../model/passwordPolicy";
import { useChangePassword } from "../hooks/useChangePassword";

export function PasswordContainer(): ReactElement {
  const { t } = useTranslation();
  const change = useChangePassword();
  const { push } = useToast();
  const [wrongCurrent, setWrongCurrent] = useState(false);
  const [doneAt, setDoneAt] = useState<string | null>(null);
  const [limited, setLimited] = useState<ApiError | null>(null);
  const waitSeconds = useRateLimitWait(limited);

  const submit = useCallback(
    async (current: string, next: string): Promise<PasswordSubmitOutcome> => {
      setWrongCurrent(false);
      setLimited(null);
      try {
        await change.mutateAsync({ current, next });
        setDoneAt(dateText(new Date(), "time"));
        push({ tone: "success", message: t("account.password.toast") });
        return { kind: "changed" };
      } catch (cause) {
        const error = asApiError(cause);
        if (isPolicyRefusal(error)) return { kind: "newRefused", message: error.message };
        if (error.status === 422) return { kind: "formRefused", message: userMessage(error, t("common.state.error")) };
        if (error.status === 401) setWrongCurrent(true);
        else if (error.status === 429) setLimited(error);
        else push({ tone: "danger", message: t("common.state.error") });
        return { kind: "refused" };
      }
    },
    [change, push, t],
  );

  return (
    <PasswordForm
      saving={change.isPending}
      wrongCurrent={wrongCurrent}
      doneAt={doneAt}
      waitSeconds={waitSeconds}
      onSubmit={submit}
    />
  );
}
